import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { Alert, ConfirmationDialog } from "klayowebshared";
import { debounce, isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Dialog } from "../../components/dialogs/Dialog";
import { SelectMenu } from "../../components/SelectMenu";
import { MultiAttributeSelector } from "../../components/selectors/MultiAttributeSelector";
import { TrainingSelector } from "../../components/selectors/TrainingSelector";
import { TextField } from "../../components/TextField";
import { COURSE_RECURRENCE, CREATE_TRAINING_MODE } from "../../constants";
import { SettingsAttribute } from "../../data/settings/SettingsAttribute";
import { CreateTrainingFormValues } from "../../interfaces/Training";
import { CreateTrainingSchema } from "../../utilities/validationSchema";
// Default form values
const defaultValues: CreateTrainingFormValues = {
  trainingName: "",
  requiredFaculties: "",
  recurrence: undefined,
  selectedCompetencies: [],
  selectedIntegrationId: undefined
};
const CreateTrainingDialog = (props: any) => {
  const {
    mode,
    onClose,
    onSave,
    theme,
    error,
    trainingData,
    isLoading,
    manualIntegrations,
    onShowNewAttributeDialog,
    onLoadAttributes,
    attributesClass
  } = props;

  const isInitialized = useRef(false);
  const [isAttributeLoading, setIsAttributeLoading] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { dirtyFields, errors },
    watch
  } = useForm<CreateTrainingFormValues>({
    resolver: zodResolver(CreateTrainingSchema),
    defaultValues
  });

  const selectedCompetencies = watch("selectedCompetencies");

  const isChangedForm =
    !isEmpty(dirtyFields) || Object.values(dirtyFields).some((isDirty) => isDirty);

  const attributes = useMemo(() => {
    const attributes = attributesClass?.attributes || [];

    if (isEmpty(selectedCompetencies)) return attributes;

    const selectedIds = selectedCompetencies.map((comp: any) => comp.id);
    return attributes.filter((attribute: any) => !selectedIds.includes(attribute.id));
  }, [attributesClass, selectedCompetencies]);

  // Search and filter with debounce
  const debouncedLoadAttributes = useCallback(
    debounce(async (searchText?: string) => {
      onFetchAttributes(searchText);
    }, 500),
    [onLoadAttributes]
  );

  // Integration items for dropdown
  const integrationItems = useMemo(() => {
    if (!manualIntegrations || !Array.isArray(manualIntegrations)) {
      return [];
    }
    return manualIntegrations.map((integration: any) => ({
      id: integration.id,
      name: integration.name || `Integration ${integration.id}`
    }));
  }, [manualIntegrations]);

  const onFetchAttributes = async (searchText?: string) => {
    setIsAttributeLoading(true);
    try {
      await onLoadAttributes(null, searchText);
    } catch (error) {
    } finally {
      setIsAttributeLoading(false);
    }
  };

  // Function to find and set recurrence
  const findAndSetRecurrence = (trainingRecurrence: number) => {
    const recurrenceValue = Object.entries(COURSE_RECURRENCE).find(
      ([key, value]) => value.value === trainingRecurrence
    );
    return recurrenceValue ? recurrenceValue[1] : { value: COURSE_RECURRENCE.NONE.value };
  };

  // Initialize data for edit mode

  useEffect(() => {
    if (
      mode === CREATE_TRAINING_MODE.EDIT &&
      trainingData &&
      attributes &&
      attributes.length > 0 &&
      !isInitialized.current
    ) {
      reset({
        trainingName: trainingData.name || "",
        requiredFaculties: trainingData.facultiesAsString || "",
        recurrence: findAndSetRecurrence(trainingData.recurrence),
        selectedCompetencies: trainingData.associatedAttribute
          ? trainingData.associatedAttribute.map((atr: any) => new SettingsAttribute(atr))
          : [],
        selectedIntegrationId: {
          id: trainingData.id,
          name: trainingData.organizationIntegrationName
        }
      });

      isInitialized.current = true;
    }
  }, [mode, trainingData, attributes, integrationItems, reset]);

  const onSubmit = (data: CreateTrainingFormValues) => {
    if (onSave) {
      const payload = {
        courseId: mode === CREATE_TRAINING_MODE.EDIT ? trainingData.id : undefined,
        organizationIntegrationId: data.selectedIntegrationId?.id,
        courseName: data.trainingName,
        faculties: data.requiredFaculties,
        recurrence: data.recurrence?.value,
        competencies: data.selectedCompetencies.map((comp: any) => comp.id)
      };
      onSave(payload, mode);
    }
  };

  // Handle closing the dialog
  const onShowWarnCloseDialog = () => (isChangedForm ? setShowConfirmClose(true) : onCloseDialog());

  const onCloseDialog = () => {
    onClose();
  };

  const onCancelClose = () => {
    setShowConfirmClose(false);
  };

  const onAcceptClose = () => {
    setShowConfirmClose(false);
    onCloseDialog();
  };

  const isEditMode = mode === CREATE_TRAINING_MODE.EDIT;

  // Initialize attributes on mount
  useEffect(() => {
    debouncedLoadAttributes();
  }, []);

  useEffect(() => {
    if (isEmpty(integrationItems) || mode === CREATE_TRAINING_MODE.EDIT) return;

    setValue("selectedIntegrationId", integrationItems[0]);
  }, [integrationItems, mode]);

  return (
    <>
      <Dialog
        open={true}
        theme={theme}
        onClose={onShowWarnCloseDialog}
        fullWidth={true}
        maxWidth='sm'
        title={isEditMode ? "Edit Training" : "Create Training"}
        actions={[
          {
            label: isEditMode ? "Save" : "Create",
            primary: true,
            variant: "filled",
            disabled: !isChangedForm,
            onClick: handleSubmit(onSubmit),
            isLoading: isLoading
          },
          {
            label: "Cancel",
            onClick: onShowWarnCloseDialog
          }
        ]}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction='column' spacing={4}>
            {error && (
              <Alert severity='error' sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}

            <Stack direction='column' spacing={1}>
              <Controller
                name='selectedIntegrationId'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TrainingSelector
                    label='Training provider (required)'
                    options={integrationItems}
                    onChange={(event: any, newValue: any) => field.onChange(newValue)}
                    placeholder='Please select an option'
                    value={field.value}
                    errorMessage={error?.message}
                    error={!!error?.message}
                    getOptionLabel={(option: any) => option.name || ""}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  />
                )}
              />
            </Stack>

            <Controller
              name='trainingName'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  value={field.value}
                  label='Name of training (required)'
                  dense={true}
                  autoComplete={false}
                  autoFocus={false}
                  sx={{ width: "100%" }}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name='requiredFaculties'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  value={field.value}
                  label='Faculties (optional)'
                  dense={true}
                  autoComplete={false}
                  sx={{ width: "100%" }}
                  onChange={(e: any) => field.onChange(e.target.value)}
                />
              )}
            />

            <Controller
              name='recurrence'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <SelectMenu
                  label='Recurrence (optional)'
                  className='employee-filter-section-menu'
                  value={field.value}
                  firstValueDefault={true}
                  dense={true}
                  disabled={false}
                  items={Object.entries(COURSE_RECURRENCE)}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "auto", md: "100%" },
                    "& fieldset legend span": { display: "none" }
                  }}
                />
              )}
            />

            <Controller
              name='selectedCompetencies'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiAttributeSelector
                  title='Competency'
                  placeHolder='Search and add competencies'
                  multiple={true}
                  values={field.value}
                  onNewItemClick={onShowNewAttributeDialog}
                  items={attributes}
                  getOptionLabel={(option: any) => option.name || ""}
                  onChange={(event: any, newValue: any) => {
                    field.onChange(newValue);
                    onFetchAttributes("");
                  }}
                  onSearch={debouncedLoadAttributes}
                  hasNewItem={true}
                  isLoading={isAttributeLoading}
                  onBlur={() => onFetchAttributes("")}
                />
              )}
            />
          </Stack>
        </form>
        {showConfirmClose && (
          <ConfirmationDialog
            title='Cancel?'
            theme={theme}
            question={`Are you sure you want to cancel this ${isEditMode ? "edit" : "create"} training?`}
            cancelButton='No'
            acceptButton='Yes'
            onCancel={onCancelClose}
            onAccept={onAcceptClose}
          />
        )}
      </Dialog>
    </>
  );
};

export default CreateTrainingDialog;
